import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  FiX,
  FiShoppingCart,
  FiHeart,
  FiArrowRight,
} from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useToast } from '../../context/ToastContext';
import QuickViewImage from './QuickViewImage';
import QuickViewDetails from './QuickViewDetails';
import { useCurrency } from '../../context/CurrencyContext';
import { getPurchaseIssue } from '../../utils/stock';

const QuickViewModal = ({ book, onClose }) => {
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToast } = useToast();
  const { getBookDiscount, getBookAmount } = useCurrency();

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!book) return null;

  const inWishlist = isInWishlist(book.id);
  const rating = Number(book.rating) || 0;
  const discount = getBookDiscount(book);
  const inStock = !getPurchaseIssue({ ...book, quantity: 1 }, getBookAmount(book));

  const handleAddToCart = () => {
    if (!inStock) return;
    addToCart(book);
    addToast(`"${book.title}" added to cart`, 'success');
  };

  const handleWishlist = () => {
    if (inWishlist) {
      removeFromWishlist(book.id);
      addToast('Removed from wishlist', 'info');
    } else {
      addToWishlist(book);
      addToast('Added to wishlist', 'success');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Quick view: ${book.title}`}
      className="quickview-overlay fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div className="quickview-modal bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden relative flex flex-col">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close quick view"
          className="absolute top-3 right-3 z-20 w-9 h-9 bg-white/95 backdrop-blur border border-gray-200 rounded-full flex items-center justify-center text-black hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all shadow-md"
        >
          <FiX className="text-lg" />
        </button>

        <div className="overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-5">
            <QuickViewImage book={book} discount={discount} />
            <QuickViewDetails
              book={book}
              rating={rating}
              discount={discount}
              inStock={inStock}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleWishlist}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            className={`flex items-center justify-center gap-2 border text-sm font-semibold py-3 px-4 rounded-xl transition-all ${
              inWishlist
                ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                : 'border-gray-300 text-black hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <FiHeart className={inWishlist ? 'fill-red-500' : ''} />
            <span className="sm:hidden">
              {inWishlist ? 'Saved' : 'Wishlist'}
            </span>
          </button>
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!inStock}
            className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold py-3 px-4 rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiShoppingCart /> Add to Cart
          </button>
          <Link
            to={`/book/${book.id}`}
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white text-sm font-semibold py-3 px-4 rounded-xl hover:bg-black transition-all shadow-md hover:shadow-lg"
          >
            View Details <FiArrowRight />
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickViewModal;
